import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Calendar, MessageSquare, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

export const ThankYouPage = () => {
  const location = useLocation();
  const { source, name } = location.state || {};

  const firstName = name?.split(' ')[0] || 'there';

  const nextSteps = {
    session_registration: [
      { icon: Calendar, text: 'Check your email for session details and calendar invite' },
      { icon: MessageSquare, text: 'Join our WhatsApp community for updates' },
      { icon: Home, text: 'Explore other sessions while you wait' }
    ],
    profile_evaluation: [
      { icon: Calendar, text: 'Expect your evaluation report within 48 hours' },
      { icon: MessageSquare, text: 'We\'ll reach out via WhatsApp for any clarifications' },
      { icon: Home, text: 'Join a free session to learn more about Europe' }
    ],
    one_on_one: [
      { icon: Calendar, text: 'Our team will contact you within 24 hours to confirm the slot' },
      { icon: MessageSquare, text: 'Prepare any specific questions you want to discuss' },
      { icon: Home, text: 'Browse our sessions to learn more before the call' }
    ],
    default: [
      { icon: Calendar, text: 'Our team will review your submission and get back to you' },
      { icon: MessageSquare, text: 'Check your email and WhatsApp for updates' },
      { icon: Home, text: 'Explore our resources while you wait' }
    ]
  };

  const steps = nextSteps[source] || nextSteps.default;

  const getHeading = () => {
    switch (source) {
      case 'session_registration':
        return "You're Registered!";
      case 'profile_evaluation':
        return "Evaluation Request Received!";
      case 'one_on_one':
        return "Call Request Submitted!";
      default:
        return "Thank You!";
    }
  };

  const getMessage = () => {
    switch (source) {
      case 'session_registration':
        return "We've sent the session details to your email. See you at the live session!";
      case 'profile_evaluation':
        return "Our experts will evaluate your profile and send you personalized recommendations within 48 hours.";
      case 'one_on_one':
        return "Our team will reach out within 24 hours to schedule your call at a convenient time.";
      default:
        return "We've received your submission and will get back to you soon.";
    }
  };

  return (
    <div data-testid="thank-you-page" className="min-h-screen pt-20 pb-16 bg-[#FFFBF8] flex items-center">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        {/* Success Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 rounded-full bg-[#0AA06A] flex items-center justify-center mx-auto animate-fade-in">
            <CheckCircle size={48} className="text-white" />
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-3xl md:text-4xl font-bold text-[#002A76] mb-4 animate-fade-in delay-100">
          {getHeading()}
        </h1>

        {/* Personalized Message */}
        <p className="text-xl text-gray-600 mb-8 animate-fade-in delay-200">
          Hey {firstName}, {getMessage()}
        </p>

        {/* Next Steps */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm mb-8 text-left animate-fade-in delay-300">
          <h2 className="text-lg font-semibold text-[#002A76] mb-6">What happens next?</h2>
          <ul className="space-y-4">
            {steps.map((step, index) => (
              <li key={index} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#002A76]/10 flex items-center justify-center flex-shrink-0">
                  <step.icon size={20} className="text-[#002A76]" />
                </div>
                <span className="text-gray-700 pt-2">{step.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in delay-400">
          <Link to="/sessions">
            <Button className="bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full px-8 py-3 font-semibold w-full sm:w-auto">
              Explore Sessions
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
          <Link to="/">
            <Button 
              variant="outline"
              className="border-2 border-[#002A76] text-[#002A76] hover:bg-[#002A76] hover:text-white rounded-full px-8 py-3 font-semibold w-full sm:w-auto"
            >
              Back to Home
            </Button>
          </Link>
        </div>

        {/* Social Proof */}
        <div className="mt-12 pt-8 border-t animate-fade-in delay-500">
          <p className="text-gray-500 text-sm mb-4">Join thousands who've started their Europe journey with TieClad</p>
          <div className="flex justify-center items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <span key={i} className="text-[#F59E0B] text-xl">★</span>
            ))}
            <span className="text-gray-600 ml-2">4.9/5 from 500+ reviews</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
